import * as SecureStore from 'expo-secure-store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const SECURE_KEY = 'eleplan_biometric_refresh_token';
const FLAG_KEY = '@eleplan/biometrics_enabled';
const USERNAME_KEY = '@eleplan/biometrics_username';
const CLIENT_ID_KEY = '@eleplan/biometrics_client_id';

// Keychain accessibility constant that prevents iCloud backup and cross-device
// migration. Without this, expo-secure-store defaults to kSecAttrAccessibleWhenUnlocked
// which IS included in iCloud Keychain backups — meaning a fresh install can restore
// a stale refresh token from a previous session that Cognito has since invalidated.
const KEYCHAIN_ACCESSIBLE = SecureStore.WHEN_PASSCODE_SET_THIS_DEVICE_ONLY;

/**
 * Write a biometric-protected value to SecureStore.
 * Tries a direct write first (safe on iOS and most Android versions).
 * Falls back to delete-then-write for Android Keystore, which does not allow
 * in-place updates of biometric-protected keys.
 */
async function writeSecureItem(key: string, value: string): Promise<void> {
  const opts = {
    requireAuthentication: true,
    authenticationPrompt: 'Confirm your identity',
    keychainAccessible: KEYCHAIN_ACCESSIBLE,
  };
  try {
    await SecureStore.setItemAsync(key, value, opts);
  } catch {
    // Direct write failed (likely Android duplicate key) — delete and recreate.
    await SecureStore.deleteItemAsync(key).catch(() => {});
    await SecureStore.setItemAsync(key, value, opts);
  }
}

/**
 * Save the refresh token to SecureStore with biometric protection.
 * On iOS this uses the Secure Enclave (Face ID / Touch ID).
 * On Android this uses the Android Keystore.
 */
export async function saveBiometricCredentials(
  refreshToken: string,
  username: string,
  clientId?: string
): Promise<void> {
  // Always delete first to migrate any old items stored without THIS_DEVICE_ONLY,
  // ensuring existing users with iCloud-backed tokens get the correct accessibility.
  await SecureStore.deleteItemAsync(SECURE_KEY).catch(() => {});
  await writeSecureItem(SECURE_KEY, refreshToken);
  await AsyncStorage.multiSet([
    [FLAG_KEY, 'true'],
    [USERNAME_KEY, username],
    [CLIENT_ID_KEY, clientId || ''],
  ]);
}

/**
 * Retrieve the stored refresh token. Triggers the biometric prompt on iOS/Android.
 * Returns null if not found or if authentication fails.
 */
export async function getBiometricCredentials(): Promise<{ refreshToken: string; username: string; clientId: string } | null> {
  try {
    const [refreshToken, username, clientId] = await Promise.all([
      SecureStore.getItemAsync(SECURE_KEY, {
        requireAuthentication: true,
        authenticationPrompt: Platform.OS === 'ios' ? 'Sign in with Face ID / Touch ID' : 'Sign in with biometrics',
      }),
      AsyncStorage.getItem(USERNAME_KEY),
      AsyncStorage.getItem(CLIENT_ID_KEY),
    ]);
    if (!refreshToken) return null;
    return { refreshToken, username: username || '', clientId: clientId || '' };
  } catch {
    return null;
  }
}

/**
 * Remove stored biometric credentials and disable the feature.
 */
export async function clearBiometricCredentials(): Promise<void> {
  try {
    await SecureStore.deleteItemAsync(SECURE_KEY);
  } catch { /* already gone */ }
  await AsyncStorage.multiRemove([FLAG_KEY, USERNAME_KEY, CLIENT_ID_KEY]);
}

/**
 * Clear only the secure refresh token blob while preserving the user's
 * biometric preference flag. This lets a normal password sign-in repopulate
 * biometrics automatically via refreshBiometricToken().
 */
export async function clearBiometricToken(): Promise<void> {
  try {
    await SecureStore.deleteItemAsync(SECURE_KEY);
  } catch {
    // already gone
  }
}

/**
 * Silently update the biometric refresh token after a successful login.
 * Called from bootstrapSession so the stored token is always the most recent one.
 *
 * Uses writeSecureItem (try-direct-write first) to avoid deleting the old valid
 * token before we know the new write will succeed. If both attempts fail, we
 * clear the enabled flag so the user sees a clean "re-enable biometrics" prompt.
 */
export async function refreshBiometricToken(refreshToken: string, clientId?: string): Promise<void> {
  try {
    const enabled = await isBiometricEnabled();
    if (!enabled) return;

    await writeSecureItem(SECURE_KEY, refreshToken);
    if (typeof clientId === 'string') {
      await AsyncStorage.setItem(CLIENT_ID_KEY, clientId);
    }
  } catch {
    // Both write attempts failed — clear the flag so the user is prompted to
    // re-enable biometrics rather than hitting a confusing "no credentials" error.
    await AsyncStorage.removeItem(FLAG_KEY).catch(() => {});
  }
}

/**
 * Check whether the user has biometric login enabled on this device.
 * Reads a plain AsyncStorage flag — no biometric prompt.
 */
export async function isBiometricEnabled(): Promise<boolean> {
  const flag = await AsyncStorage.getItem(FLAG_KEY);
  return flag === 'true';
}
